import { ServerInterface } from '@/services/Server/Interfaces';
import { ServerStatus } from '@/services/Server';

export const QUICK_CREATE_DELAY_MS = 500;
export const QUICK_CREATE_MAX_COUNT = 50;
export const QUICK_CREATE_DEFAULT_SUFFIX = '.example.test';

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function sanitizeBatchId(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

export function generateBatchId(): string {
  const suffix = Math.random().toString(36).replace(/[^a-z0-9]/g, '').slice(0, 6);
  return `qg-${suffix || 'batch'}`;
}

export function normalizeDomainSuffix(suffix: string): string {
  const trimmed = (suffix || '').trim().toLowerCase();
  if (!trimmed) {
    return QUICK_CREATE_DEFAULT_SUFFIX;
  }
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

export function padIndex(index: number, count: number): string {
  const width = Math.max(2, String(count).length);
  const value = String(index);
  if (value.length >= width) {
    return value;
  }
  return new Array(width - value.length + 1).join('0') + value;
}

export function buildBatchDomain(batchId: string, index: number, count: number, suffix: string): string {
  const id = sanitizeBatchId(batchId);
  const domainSuffix = normalizeDomainSuffix(suffix);
  return `${id}-${padIndex(index, count)}${domainSuffix}`;
}

export function buildBatchDomains(batchId: string, count: number, suffix: string): string[] {
  const safeCount = Math.max(1, Math.min(QUICK_CREATE_MAX_COUNT, Math.floor(count) || 1));
  const domains: string[] = [];
  for (let i = 1; i <= safeCount; i += 1) {
    domains.push(buildBatchDomain(batchId, i, safeCount, suffix));
  }
  return domains;
}

export function upServers(servers: ServerInterface[]): ServerInterface[] {
  return (servers || []).filter((server) => server.status === ServerStatus.Up);
}

export function sortServersByUpFirst(servers: ServerInterface[]): ServerInterface[] {
  return (servers || []).slice().sort((a, b) => {
    if (a.status === ServerStatus.Up && b.status !== ServerStatus.Up) {
      return -1;
    }
    if (b.status === ServerStatus.Up && a.status !== ServerStatus.Up) {
      return 1;
    }
    return (a.hostname || '').localeCompare(b.hostname || '');
  });
}

export function pickRandomUpServer(servers: ServerInterface[]): ServerInterface | undefined {
  const pool = upServers(servers);
  if (!pool.length) {
    return undefined;
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

export function pickRandomUpServers(servers: ServerInterface[], count: number): ServerInterface[] {
  const pool = upServers(servers);
  if (!pool.length) {
    return [];
  }

  const picks: ServerInterface[] = [];
  for (let i = 0; i < count; i += 1) {
    picks.push(pool[Math.floor(Math.random() * pool.length)]);
  }
  return picks;
}

export function pickRandomItem<T>(items: T[]): T | undefined {
  if (!items || !items.length) {
    return undefined;
  }
  return items[Math.floor(Math.random() * items.length)];
}
